"use client";

import { useCallback, useState } from "react";
import { RefreshCw, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";
import { trackEvent } from "@/lib/track";

interface Slip {
  quote: string;
  numbers: number[];
}

/**
 * 首屏那张能真正点开的签条。
 *
 * 为什么存在:这一页的副文案写着 "Tap once for a random fortune",而在此之前
 * 整个首屏没有任何可以 tap 的东西 —— 四张模式卡全是链接。想要一条随机签语的人
 * 要再点一次才拿得到任何东西,文案许下的承诺一屏之内没有兑现。
 *
 * 为什么不调 AI:generator scope 的访客额度是一天 3 条,让这里白白吃掉一条,
 * 真正想用 AI 的人到了子页面反而没得用。这里直接从 lib/fortune-data 的本地
 * 签语里随机取,于是它不限次 —— 页面上"keep tapping until one lands"那句话
 * 也才真的成立。想要 AI 现写的,下面的 Quick Fortune 就是入口。
 *
 * 数据用动态 import 拉取,和首页兜底同一套做法:这份文案不参与首屏渲染,
 * 没必要进初始 bundle。
 */
export function InstantFortuneSlip() {
  const [slip, setSlip] = useState<Slip | null>(null);
  const [loading, setLoading] = useState(false);

  const draw = useCallback(async () => {
    setLoading(true);
    try {
      const { fortunes } = await import("@/lib/fortune-data");
      // 避开刚看过的那条,否则连点两下抽到同一句会让人以为按钮坏了。
      const pool =
        slip && fortunes.length > 1
          ? fortunes.filter((f) => f.quote !== slip.quote)
          : fortunes;
      const pick = pool[Math.floor(Math.random() * pool.length)];
      if (pick) {
        setSlip({ quote: pick.quote, numbers: pick.numbers });
        trackEvent("fortune_drawn", {
          surface: "generator_hub",
          source: "local",
        });
      }
    } finally {
      setLoading(false);
    }
  }, [slip]);

  return (
    <div className="mx-auto mb-12 max-w-2xl">
      <button
        type="button"
        onClick={draw}
        disabled={loading}
        aria-label={
          slip ? "Draw another random fortune" : "Draw a random fortune"
        }
        className={cn(
          // 刻意不用这一页其它区块那套 rounded-2xl + backdrop-blur:签条要看着
          // 像从饼干里抽出来的纸,不像又一张功能卡。
          "group relative w-full overflow-hidden rounded-lg px-6 py-7 text-center transition-all",
          "bg-[linear-gradient(180deg,#fffdf7_0%,#fff8e8_100%)] dark:bg-[linear-gradient(180deg,#2a2318_0%,#1f1a12_100%)]",
          "border border-dashed border-amber-400/70 dark:border-amber-500/40",
          "hover:border-solid hover:border-amber-500 hover:shadow-lg",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2",
          "disabled:cursor-wait",
        )}
      >
        {slip ? (
          <>
            <p className="font-serif text-lg italic leading-relaxed text-slate-800 dark:text-amber-50 md:text-xl">
              &ldquo;{slip.quote}&rdquo;
            </p>
            {slip.numbers.length > 0 && (
              <p className="mt-4 flex flex-wrap items-center justify-center gap-2">
                <span className="sr-only">Lucky numbers:</span>
                {slip.numbers.map((n) => (
                  <span
                    key={n}
                    className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-gradient-to-br from-amber-400 to-orange-500 text-xs font-semibold text-white"
                  >
                    {n}
                  </span>
                ))}
              </p>
            )}
            <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-medium text-amber-700 dark:text-amber-300">
              <RefreshCw
                className={cn("h-3.5 w-3.5", loading && "animate-spin")}
                aria-hidden="true"
              />
              Crack another
            </span>
          </>
        ) : (
          <>
            <span className="inline-flex items-center gap-2 text-lg font-semibold text-slate-800 dark:text-amber-50">
              <Sparkles className="h-5 w-5 text-amber-500" aria-hidden="true" />
              Tap for a random fortune
            </span>
            <span className="mt-2 block text-sm text-slate-500 dark:text-slate-400">
              No settings, no signup, no limit — tap as many times as you like.
            </span>
          </>
        )}
      </button>

      {slip && (
        <p className="mt-3 text-center text-sm text-slate-500 dark:text-slate-400">
          Want one written for you by AI instead? That is what the modes below
          are for.
        </p>
      )}
    </div>
  );
}
